import React, { useState, useEffect } from "react";
import { apiService } from "../../utils/api";
import { FaCheck, FaEdit, FaTimes, FaTrash, FaSearch } from "react-icons/fa";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, IconButton, Switch, Box, Typography, Button, TextField, InputAdornment } from "@mui/material";
import "./StudentManagement.css";

function StudentManagement() {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStudent, setNewStudent] = useState({
    username: "",
    email: "",
    password: "",
  });

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("table");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await apiService.getStudents();
      if (response.data.success) {
        setStudents(response.data.students);
      } else {
        setError(response.data.message || "Failed to fetch students");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error connecting to server");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewStudent((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");

    if (!isEditing && (!newStudent.username || !newStudent.email || !newStudent.password)) {
      setFormError("All fields are required");
      return;
    }

    if (isEditing && (!newStudent.username || !newStudent.email)) {
      setFormError("Username and email are required");
      return;
    }

    try {
      setSubmitting(true);
      let response;

      if (isEditing) {
        response = await apiService.updateStudent(newStudent.id, newStudent);
      } else {
        response = await apiService.addStudent(newStudent);
      }

      if (response.data.success) {
        setFormSuccess(isEditing ? "Student updated successfully!" : "Student added successfully!");
        setNewStudent({
          username: "",
          email: "",
          password: "",
        });
        setShowAddForm(false);
        setIsEditing(false);
        fetchStudents();
      } else {
        setFormError(response.data.message || (isEditing ? "Failed to update student" : "Failed to add student"));
      }
    } catch (err) {
      setFormError(err.response?.data?.message || "Error connecting to server");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const showUpdateForm = (student) => {
    setNewStudent({
      id: student.id,
      username: student.username,
      email: student.email,
      password: "", // Don't include the password when editing
    });
    setIsEditing(true);
    setShowAddForm(true);
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm("Are you sure you want to delete this student?")) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.deleteStudent(studentId);

      if (response.data.success) {
        setFormSuccess("Student deleted successfully!");
        fetchStudents();
      } else {
        setError(response.data.message || "Failed to delete student");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error connecting to server");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const cancelForm = () => {
    setNewStudent({
      username: "",
      email: "",
      password: "",
    });
    setIsEditing(false);
    setShowAddForm(false);
    setFormError("");
    setFormSuccess("");
  };

  const handleStatusChange = async (studentId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "blocked" : "active";
    const actionText = newStatus === "active" ? "unblock" : "block";

    if (!window.confirm(`Are you sure you want to ${actionText} this student?`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.updateUserStatus(studentId, newStatus);

      if (response.data.success) {
        setFormSuccess(`Student ${newStatus === "active" ? "unblocked" : "blocked"} successfully!`);
        fetchStudents();
      } else {
        setError(response.data.message || `Failed to ${actionText} student`);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error connecting to server");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Filter students based on search term
  const filteredStudents = students.filter((student) => student.username.toLowerCase().includes(searchTerm.toLowerCase()) || student.email.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div className="student-management">
      <div className="section-header">
        <h2>Student Management</h2>
        <button
          className="add-button"
          onClick={() => {
            if (showAddForm) {
              cancelForm();
            } else {
              setShowAddForm(true);
            }
          }}
        >
          {showAddForm ? "Cancel" : "Add Student"}
        </button>
      </div>

      {showAddForm && (
        <div className="add-student-form">
          <h3>{isEditing ? "Edit Student" : "Add New Student"}</h3>
          {formError && <p className="error-message">{formError}</p>}
          {formSuccess && <p className="success-message">{formSuccess}</p>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Username:</label>
              <input type="text" name="username" value={newStudent.username} onChange={handleInputChange} placeholder="Enter username" />
            </div>

            <div className="form-group">
              <label>Email:</label>
              <input type="email" name="email" value={newStudent.email} onChange={handleInputChange} placeholder="Enter email" />
            </div>

            {!isEditing && (
              <div className="form-group">
                <label>Password:</label>
                <input type="password" name="password" value={newStudent.password} onChange={handleInputChange} placeholder="Enter password" />
              </div>
            )}

            {isEditing && (
              <div className="form-group">
                <label>New Password (leave blank to keep current):</label>
                <input type="password" name="password" value={newStudent.password} onChange={handleInputChange} placeholder="Enter new password" />
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="submit-button" disabled={submitting}>
                {submitting ? "Please wait..." : isEditing ? "Update Student" : "Add Student"}
              </button>

              <button type="button" className="cancel-button" onClick={cancelForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading students...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p className="error-message">{error}</p>
          <button onClick={fetchStudents} className="retry-button">
            Try Again
          </button>
        </div>
      ) : (
        <div className="students-list">
          <div className="list-header">
            <h3>Current Students</h3>

            <div className="list-controls">
              <div className="search-container">
                <FaSearch className="search-icon" />
                <input type="text" placeholder="Search students..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
              </div>
            </div>
          </div>

          {filteredStudents.length === 0 ? (
            <div className="empty-state">
              <p>No students found.</p>
              {searchTerm && <p>Try adjusting your search criteria.</p>}
            </div>
          ) : viewMode === "table" ? (
            <Box sx={{ width: "100%" }}>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="student table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Username</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Batch</TableCell>
                      <TableCell>Roll No</TableCell>
                      <TableCell>Created At</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStudents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((student) => (
                      <TableRow
                        key={student.id}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          backgroundColor: student.status === "blocked" ? "rgba(0, 0, 0, 0.04)" : "inherit",
                        }}
                      >
                        <TableCell>{student.username}</TableCell>
                        <TableCell>{student.email}</TableCell>
                        <TableCell>{student.enrolledBatches && student.enrolledBatches.length > 0 ? student.enrolledBatches.map((batch) => batch.name).join(", ") : "No batch assigned"}</TableCell>
                        <TableCell>{student.rollNo}</TableCell>
                        <TableCell>{new Date(student.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Switch checked={student.status === "active"} onChange={() => handleStatusChange(student.id, student.status)} color="primary" />
                            <Typography variant="body2" color={student.status === "active" ? "success.main" : "error.main"}>
                              {student.status === "active" ? "Active" : "Blocked"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <IconButton onClick={() => showUpdateForm(student)} color="primary" size="small">
                              <FaEdit />
                            </IconButton>
                            <IconButton onClick={() => handleDeleteStudent(student.id)} color="error" size="small">
                              <FaTrash />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div" count={filteredStudents.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
            </Box>
          ) : (
            <div className="student-cards">
              {filteredStudents.map((student) => (
                <div key={student.id} className={`student-card ${student.status === "blocked" ? "blocked-card" : ""}`}>
                  <div className="card-header">
                    <div className="student-avatar">{student.username.charAt(0).toUpperCase()}</div>
                    <div className="student-info">
                      <h4 className="student-name">{student.username}</h4>
                      <p className="student-email">{student.email}</p>
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="info-row">
                      <span className="info-label">Batch:</span>
                      <span className="info-value">{student.enrolledBatches && student.enrolledBatches.length > 0 ? student.enrolledBatches.map((batch) => batch.name).join(", ") : "No batch assigned"}</span>
                    </div>

                    <div className="info-row">
                      <span className="info-label">Created:</span>
                      <span className="info-value">{new Date(student.createdAt).toLocaleDateString()}</span>
                    </div>

                    <div className="info-row">
                      <span className="info-label">Status:</span>
                      <div className="status-switch-container">
                        <label className="switch">
                          <input type="checkbox" checked={student.status === "active"} onChange={() => handleStatusChange(student.id, student.status)} />
                          <span className="slider round"></span>
                        </label>
                        <span className={`status-label ${student.status}`}>
                          {student.status === "active" ? (
                            <>
                              <FaCheck className="status-icon active" /> Active
                            </>
                          ) : (
                            <>
                              <FaTimes className="status-icon blocked" /> Blocked
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="card-actions">
                    <button onClick={() => showUpdateForm(student)} className="action-button edit">
                      <FaEdit />
                      Edit
                    </button>
                    <button onClick={() => handleDeleteStudent(student.id)} className="action-button delete">
                      <FaTrash />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default StudentManagement;
